// ModelCard — renders an ATAD-146 model-file leaf in the expression tree.
//
// A model scores the whole US population 1–20 (1 = best), so the card is a
// membership card (same frame and the same drag-drop wiring as
// MembershipCard, via useMembershipCardDnd) plus the score range, which is
// required: the leaf means "people whose score is in this range".
//
// The range is the builder's existing Range input. Range reports its starting
// value as soon as it mounts, so it is always given the leaf's own range as
// `initialState` (reported with loadingInitialState): without it, Range would
// report the full 1–20 instead, overwriting a saved audience's range and
// marking it as changed the moment it loads. It is reversed so 1 (best) sits
// on the right: the card reads 20 → 1.

import Range from './components/Range';
import { useMembershipCardDnd, type MembershipCardProps } from './MembershipCard';
import {
  MODEL_SCORE_MAX,
  MODEL_SCORE_MIN,
} from '../../helpers/expressionTree';
import {
  REMOVE_MY_DATA_RESOURCE,
  SET_MY_DATA_RESOURCE_STATE,
} from '../../reducers/builderReducer';
import type { ModelLeaf } from '../../types/dataDictionary';

/** Range reads its domain from dictionary-style string attrs. */
const SCORE_ATTRS = { min: String(MODEL_SCORE_MIN), max: String(MODEL_SCORE_MAX) };

export const MODEL_SCORE_LABEL = '1 = best';

export type ModelCardProps = Omit<MembershipCardProps, 'leaf'> & {
  leaf: ModelLeaf;
};

export default function ModelCard({
  leaf,
  zoneType,
  builderState,
  builderDispatch,
}: ModelCardProps) {
  const { connect, isDragging, isHighlighted } = useMembershipCardDnd({
    leaf,
    zoneType,
    builderState,
    builderDispatch,
  });

  // By nodeId as well as fileId: the same model can be in a zone twice with
  // different ranges, and × must remove this card, not the first match.
  const handleRemove = () =>
    builderDispatch({
      type: REMOVE_MY_DATA_RESOURCE,
      payload: { id: leaf.fileId, type: 'model', zoneType, nodeId: leaf.nodeId },
    });

  const setScoreRange = (
    range: { min: number; max: number },
    config?: { loadingInitialState?: boolean },
  ) =>
    builderDispatch({
      type: SET_MY_DATA_RESOURCE_STATE,
      payload: {
        nodeId: leaf.nodeId,
        zoneType,
        state: range,
        loadingInitialState: config?.loadingInitialState,
      },
    });

  const label = leaf.name || leaf.fileId;

  return (
    <div
      ref={connect}
      className={[
        'rounded-lg bg-white p-3 border transition-colors relative',
        isDragging ? 'opacity-50' : '',
        isHighlighted ? 'border-2 border-dashed border-primary' : 'border-gray-200',
      ].join(' ')}
    >
      {isHighlighted && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-primary text-white px-2 py-0.5 rounded-full whitespace-nowrap">
          drop to group
        </div>
      )}
      <button
        type="button"
        onClick={handleRemove}
        aria-label={`Remove ${label}`}
        title={`Remove ${label}`}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-base leading-none px-1.5 py-0.5 rounded hover:bg-gray-100"
      >
        ×
      </button>
      <div className="text-xs text-gray-500 mb-1 truncate uppercase tracking-wide pr-6">
        Model File
      </div>
      <div className="flex flex-row items-center w-full pr-6 mb-2">
        <span className="font-medium text-gray-900 break-all">{label}</span>
      </div>
      <Range
        id={leaf.nodeId}
        name="Score"
        attrs={SCORE_ATTRS}
        label={MODEL_SCORE_LABEL}
        initialState={{ min: leaf.scoreMin, max: leaf.scoreMax }}
        setVariablesState={setScoreRange}
        reverse
      />
    </div>
  );
}
